import { localTime } from "./localTime";

/**
 * The four exchange places of the analysis — New York, London, Frankfurt, Tokyo: their time zone, the opening of the
 * cash exchange (NYSE 09:30, LSE 08:00, Xetra 09:00, TSE 09:00, local time) and the bank holidays.
 * A time counts by the place's own local date. Pure; the holidays are computed per year and cached.
 */

export const PLACES = ["newYork", "london", "frankfurt", "tokyo"] as const;
export type Place = (typeof PLACES)[number];

/** Each place's time zone and the opening of its cash exchange (minute of the local day). */
export const PLACE_INFO: Record<Place, { timeZone: string; open: number }> = {
  newYork: { timeZone: "America/New_York", open: 9 * 60 + 30 },
  london: { timeZone: "Europe/London", open: 8 * 60 },
  frankfurt: { timeZone: "Europe/Berlin", open: 9 * 60 },
  tokyo: { timeZone: "Asia/Tokyo", open: 9 * 60 },
};

const ymd = (y: number, m: number, d: number) => `${y}-${String(m).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
const weekday = (y: number, m: number, d: number) => new Date(Date.UTC(y, m - 1, d)).getUTCDay();
const shift = (y: number, m: number, d: number, days: number) => {
  const x = new Date(Date.UTC(y, m - 1, d + days));
  return [x.getUTCFullYear(), x.getUTCMonth() + 1, x.getUTCDate()] as const;
};
/** The n-th weekday (0 = Sunday) of a month; n = −1 = the last. */
function nthWeekday(y: number, m: number, wd: number, n: number): number {
  if (n > 0) return 1 + ((wd - weekday(y, m, 1) + 7) % 7) + 7 * (n - 1);
  const last = new Date(Date.UTC(y, m, 0)).getUTCDate();
  return last - ((weekday(y, m, last) - wd + 7) % 7);
}
/** Easter Sunday (Gregorian calendar, the anonymous algorithm). */
function easter(y: number): readonly [number, number] {
  const a = y % 19;
  const b = Math.floor(y / 100);
  const c = y % 100;
  const d = Math.floor(b / 4);
  const e = b % 4;
  const f = Math.floor((b + 8) / 25);
  const g = Math.floor((b - f + 1) / 3);
  const h = (19 * a + b - d - g + 15) % 30;
  const i = Math.floor(c / 4);
  const k = c % 4;
  const l = (32 + 2 * e + 2 * i - h - k) % 7;
  const m = Math.floor((a + 11 * h + 22 * l) / 451);
  return [Math.floor((h + l - 7 * m + 114) / 31), ((h + l - 7 * m + 114) % 31) + 1];
}
const fromEaster = (y: number, days: number) => {
  const [m, d] = easter(y);
  return ymd(...shift(y, m, d, days));
};

/** New York: the Federal Reserve's holidays; a fixed-date holiday on a weekend is observed on Friday / Monday. */
function newYork(y: number): string[] {
  const observed = (m: number, d: number) => {
    const wd = weekday(y, m, d);
    let days = 0;
    if (wd === 6) days = -1;
    else if (wd === 0) days = 1;
    return ymd(...shift(y, m, d, days));
  };
  return [
    observed(1, 1),
    ymd(y, 1, nthWeekday(y, 1, 1, 3)),
    ymd(y, 2, nthWeekday(y, 2, 1, 3)),
    ymd(y, 5, nthWeekday(y, 5, 1, -1)),
    observed(6, 19),
    observed(7, 4),
    ymd(y, 9, nthWeekday(y, 9, 1, 1)),
    ymd(y, 10, nthWeekday(y, 10, 1, 2)),
    observed(11, 11),
    ymd(y, 11, nthWeekday(y, 11, 4, 4)),
    observed(12, 25),
  ];
}

/** London (England & Wales): a holiday on a weekend moves to the next weekday not already taken. */
function london(y: number): string[] {
  const out: string[] = [];
  const substitute = (m: number, d: number) => {
    let [yy, mm, dd] = [y, m, d];
    while (weekday(yy, mm, dd) === 0 || weekday(yy, mm, dd) === 6 || out.includes(ymd(yy, mm, dd))) [yy, mm, dd] = shift(yy, mm, dd, 1);
    out.push(ymd(yy, mm, dd));
  };
  substitute(1, 1);
  out.push(fromEaster(y, -2), fromEaster(y, 1));
  out.push(ymd(y, 5, nthWeekday(y, 5, 1, 1)), ymd(y, 5, nthWeekday(y, 5, 1, -1)), ymd(y, 8, nthWeekday(y, 8, 1, -1)));
  substitute(12, 25);
  substitute(12, 26);
  return out;
}

/**
 * Frankfurt (Hesse): the public holidays — New Year, Good Friday, Easter Monday, Labour Day, Ascension, Whit Monday,
 * Corpus Christi, German Unity Day, Christmas and Boxing Day (no substitute days in Germany) — and the banks' closure
 * on 24 and 31 December.
 */
function frankfurt(y: number): string[] {
  return [ymd(y, 1, 1), fromEaster(y, -2), fromEaster(y, 1), ymd(y, 5, 1), fromEaster(y, 39), fromEaster(y, 50), fromEaster(y, 60), ymd(y, 10, 3), ymd(y, 12, 24), ymd(y, 12, 25), ymd(y, 12, 26), ymd(y, 12, 31)];
}

/**
 * Tokyo: the national holidays (equinoxes by the astronomical approximation valid 1980–2099), the substitute holiday
 * (a holiday on a Sunday moves to the next day that is no holiday), the citizens' holiday (a day between two holidays)
 * and the banks' year-end closure (31 December – 3 January).
 */
function tokyo(y: number): string[] {
  const n = y - 1980;
  const spring = Math.floor(20.8431 + 0.242194 * n - Math.floor(n / 4));
  const autumn = Math.floor(23.2488 + 0.242194 * n - Math.floor(n / 4));
  const days: [number, number][] = [
    [1, 1],
    [1, nthWeekday(y, 1, 1, 2)],
    [2, 11],
    [2, 23],
    [3, spring],
    [4, 29],
    [5, 3],
    [5, 4],
    [5, 5],
    [7, nthWeekday(y, 7, 1, 3)],
    [8, 11],
    [9, nthWeekday(y, 9, 1, 3)],
    [9, autumn],
    [10, nthWeekday(y, 10, 1, 2)],
    [11, 3],
    [11, 23],
  ];
  const set = new Set(days.map(([m, d]) => ymd(y, m, d)));
  // the citizens' holiday: a day between two holidays
  for (const [m, d] of days) {
    const between = shift(y, m, d, 1);
    if (set.has(ymd(...shift(y, m, d, 2))) && weekday(...between) !== 0) set.add(ymd(...between));
  }
  // the substitute holiday
  for (const [m, d] of days) {
    if (weekday(y, m, d) !== 0) continue;
    let next = shift(y, m, d, 1);
    while (set.has(ymd(...next))) next = shift(...next, 1);
    set.add(ymd(...next));
  }
  for (const [m, d] of [[1, 2], [1, 3], [12, 31]] as const) set.add(ymd(y, m, d));
  return [...set];
}

const BUILD: Record<Place, (y: number) => string[]> = { newYork, london, frankfurt, tokyo };
const cache = new Map<string, Set<string>>();

/** The bank holidays of a place in a year ("yyyy-mm-dd"). */
function bankHolidays(place: Place, y: number): Set<string> {
  const key = `${place}:${y}`;
  let s = cache.get(key);
  if (!s) cache.set(key, (s = new Set(BUILD[place](y))));
  return s;
}

/** Whether time t falls on a bank holiday of the place (its own local date). */
export function isBankHoliday(place: Place, t: number): boolean {
  const day = localTime(t, PLACE_INFO[place].timeZone).date;
  return bankHolidays(place, Number(day.slice(0, 4))).has(day);
}

/** Whether the place does business at time t: a weekday that is no bank holiday there. */
export function isBusinessDay(place: Place, t: number): boolean {
  const lt = localTime(t, PLACE_INFO[place].timeZone);
  return lt.weekday >= 1 && lt.weekday <= 5 && !bankHolidays(place, Number(lt.date.slice(0, 4))).has(lt.date);
}
